/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Button, Breadcrumb } from "flowbite-react";
import { HiPlus, HiHome } from "react-icons/hi";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetWorkShiftsQuery, useCreateWorkShiftMutation } from "@/api/agentApi/workShift";
import WorkShiftListTable from "@/components/workShifts/WorkShiftListTable";
import WorkShiftFormDialog from "@/components/workShifts/WorkShiftFormDialog";
import WorkShiftFilters from "@/components/workShifts/WorkShiftFilters";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { DataGridPagination } from "@/sharedTypes";
import LoadingPage from "@/components/LoadingPage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

const WorkShiftList = () => {
    const { t } = useTranslation(["workShift", "common"]);
    const user = useCurrentUser();
    const isAdmin = user?.agent_role === agentRoles.company_admin;
    const [page, setPage] = useState(1);
    const [showInactive, setShowInactive] = useState(false);
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { data: workShiftsData, isLoading } = useGetWorkShiftsQuery({
        pagination: {
            page,
            pageSize: 15,
        },
        filters: {
            ...filters,
            is_active: showInactive ? false : true,
        }
    });
    
    const [createWorkShift] = useCreateWorkShiftMutation();

    const handleCreate = (data: any) => {
        createWorkShift(data)
            .unwrap()
            .then(() => {
                toast.success(t("workShift:createSuccess"));
                setIsCreateModalOpen(false);
            })
            .catch((err) => toast.error(getErrorMessage(err)));
    };

    const handleFilterChange = (newFilters: Record<string, any>) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page
    };
    
    const handleToggleInactive = (value: boolean) => {
        setShowInactive(value);
        setPage(1);
    };

    const dataSource = workShiftsData?.items || workShiftsData?.data || workShiftsData || [];
    const tableData = Array.isArray(dataSource) ? dataSource : [];
    const pagination = workShiftsData?.pagination || {};

    return (
        <NavbarSidebarLayout>
            {isLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
                <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
                    <div className="col-span-full space-y-6">
                        <Breadcrumb className="mb-4">
                            <Breadcrumb.Item>
                                <Link to="/" className="flex items-center gap-x-3">
                                  <HiHome className="text-xl" />
                                  <span className="dark:text-white">{t("common:home")}</span>
                                </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                {t("workShift:title")}
                            </Breadcrumb.Item>
                        </Breadcrumb>
                        
                        <div className="flex items-center justify-between">
                             <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                                    {t("workShift:title")}
                                </h1>
                             </div>
                            
                            {isAdmin && (
                                <Button
                                    color="primary"
                                    onClick={() => setIsCreateModalOpen(true)}
                                >
                                    <HiPlus className="mr-2 h-4 w-4" />
                                    {t("workShift:addWorkShift")}
                                </Button>
                            )}
                        </div>
                        
                        <WorkShiftFilters 
                            showInactive={showInactive}
                            onToggleInactive={handleToggleInactive}
                            onFilterChange={handleFilterChange}
                        />

                        <WorkShiftListTable
                            data={tableData}
                            pagination={pagination as DataGridPagination}
                            onPageChange={setPage}
                            showInactive={showInactive}
                         />
                    </div>
                </div>
            </div>}

             <WorkShiftFormDialog
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreate}
                mode="create"
            />
        </NavbarSidebarLayout>
    );
};


export default WorkShiftList;
